import type { Theme, SxProps, Breakpoint } from '@mui/material/styles';

import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import { List, Collapse } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ListItemButton from '@mui/material/ListItemButton';
import Drawer, { drawerClasses } from '@mui/material/Drawer';

import { usePathname } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { varAlpha } from 'src/theme/styles';

import { Logo } from 'src/components/logo';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';

import { WorkspacesPopover } from '../components/workspaces-popover';

export type NavContentProps = {
  data: {
    path: string;
    title: string;
    icon: React.ReactNode;
    info?: React.ReactNode;
    key?: string;
    subItems?: NavContentProps['data'] & { key: string };
  }[];
  slots?: {
    topArea?: React.ReactNode;
    bottomArea?: React.ReactNode;
  };
  sx?: SxProps<Theme>;
};

export function NavDesktop({
  sx,
  data,
  slots,
  layoutQuery,
}: NavContentProps & { layoutQuery: Breakpoint }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        pt: 2.5,
        px: 2.5,
        top: 0,
        left: 0,
        height: 1,
        display: 'none',
        position: 'fixed',
        flexDirection: 'column',
        bgcolor: 'var(--layout-nav-bg)',
        zIndex: 'var(--layout-nav-zIndex)',
        width: 'var(--layout-nav-vertical-width)',
        borderRight: `1px solid var(--layout-nav-border-color, ${varAlpha(theme.vars.palette.grey['500Channel'], 0.12)})`,
        [theme.breakpoints.up(layoutQuery)]: {
          display: 'flex',
        },
        ...sx,
      }}
    >
      <NavContent data={data} slots={slots} />
    </Box>
  );
}

export function NavMobile({
  sx,
  data,
  open,
  slots,
  onClose,
}: NavContentProps & { open: boolean; onClose: () => void }) {
  const pathname = usePathname();

  useEffect(() => {
    if (open) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      sx={{
        [`& .${drawerClasses.paper}`]: {
          pt: 2.5,
          px: 2.5,
          overflow: 'unset',
          bgcolor: 'var(--layout-nav-bg)',
          width: 'var(--layout-nav-mobile-width)',
          ...sx,
        },
      }}
    >
      <NavContent data={data} slots={slots} />
    </Drawer>
  );
}

export function NavContent({ data, slots, sx }: NavContentProps) {
  const pathname = usePathname();

  const [openSubItems, setOpenSubItems] = useState(false);

  useEffect(() => {
    if (pathname.includes('/campaigns') || pathname.includes('/content') || pathname.includes('/users') || pathname.includes('/audiences') || pathname.includes('/events')) {
      setOpenSubItems(true);
    }
  }, [pathname]);

  return (
    <>
      <Logo />

      {slots?.topArea}

      <WorkspacesPopover sx={{ my: 2 }} />

      <Scrollbar fillContent>
        <Box component="nav" display="flex" flex="1 1 auto" flexDirection="column" sx={sx}>
          <Box component="ul" gap={0.5} display="flex" flexDirection="column">
            {data.map((item) => {
              const isSubItems = item?.subItems && item?.subItems?.length > 0;

              if (isSubItems) {
                const isActived = item?.subItems?.some(
                  (subItem) => subItem.key === pathname.split('/').pop()
                );

                return (
                  <>
                    <ListItemButton
                      disableGutters
                      onClick={() => setOpenSubItems(!openSubItems)}
                      sx={{
                        pl: 2,
                        py: 1,
                        gap: 2,
                        pr: 1.5,
                        borderRadius: 0.75,
                        typography: 'body2',
                        fontWeight: 'fontWeightMedium',
                        color: 'var(--layout-nav-item-color)',
                        ...(isActived && {
                          fontWeight: 'fontWeightSemiBold',
                          bgcolor: 'var(--layout-nav-item-active-bg)',
                          color: 'var(--layout-nav-item-active-color)',
                          '&:hover': {
                            bgcolor: 'var(--layout-nav-item-hover-bg)',
                          },
                        }),
                      }}
                    >
                      {item.icon}
                      {item.title}
                      {openSubItems ? (
                        <Iconify icon="eva:chevron-up-fill" width={20} height={20} />
                      ) : (
                        <Iconify icon="eva:chevron-down-fill" width={20} height={20} />
                      )}
                    </ListItemButton>
                    <Collapse in={openSubItems} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item?.subItems?.map((subItem) => {
                          const isSubItemActived =
                            subItem.path === pathname ||
                            (subItem.path !== '/dashboard' && pathname.startsWith(subItem.path));
                          return (
                            <ListItemButton
                              component={RouterLink}
                              href={subItem.path}
                              sx={{
                                ml: 5,
                                borderRadius: 0.75,
                                typography: 'body2',
                                fontWeight: 'fontWeightMedium',
                                color: 'var(--layout-nav-item-color)',
                                minHeight: 'var(--layout-nav-item-height)',
                                ...(isSubItemActived && {
                                  fontWeight: 'fontWeightSemiBold',
                                  bgcolor: 'var(--layout-nav-item-active-bg)',
                                  color: 'var(--layout-nav-item-active-color)',
                                  '&:hover': {
                                    bgcolor: 'var(--layout-nav-item-hover-bg)',
                                  },
                                }),
                              }}
                              key={subItem.title}
                            >
                              <Box component="span" sx={{ width: 24, height: 24, paddingRight: 4 }}>
                                {subItem.icon}
                              </Box>
                              <Box component="span" flexGrow={1}>
                                {subItem.title}
                              </Box>
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Collapse>
                  </>
                );
              }

              const isActived =
                item.path === pathname ||
                (item.path !== '/dashboard' && pathname.startsWith(item.path));

              return (
                <ListItem disableGutters disablePadding key={item.title}>
                  <ListItemButton
                    disableGutters
                    component={RouterLink}
                    href={item.path}
                    sx={{
                      pl: 2,
                      py: 1,
                      gap: 2,
                      pr: 1.5,
                      borderRadius: 0.75,
                      typography: 'body2',
                      fontWeight: 'fontWeightMedium',
                      color: 'var(--layout-nav-item-color)',
                      minHeight: 'var(--layout-nav-item-height)',
                      ...(isActived && {
                        fontWeight: 'fontWeightSemiBold',
                        bgcolor: 'var(--layout-nav-item-active-bg)',
                        color: 'var(--layout-nav-item-active-color)',
                        '&:hover': {
                          bgcolor: 'var(--layout-nav-item-hover-bg)',
                        },
                      }),
                    }}
                  >
                    <Box component="span" sx={{ width: 24, height: 24 }}>
                      {item.icon}
                    </Box>

                    <Box component="span" flexGrow={1}>
                      {item.title}
                    </Box>

                    {item.info && item.info}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </Box>
        </Box>
      </Scrollbar>

      {slots?.bottomArea}
    </>
  );
}
